import { useCentralApi, useCurrentUser } from "hooks/shared";
import { FileOrLink, IFileAttachment } from "models/attachments"
import { GridMetaData, createUrlSearchParams } from "models/grids";
import { useState } from "react";

interface ISearchAttachmentsResult {
    attachments: IFileAttachment[];
    total: number;
};

type SearchParameters = {
    fileOrLink: FileOrLink,
    typeId: number,
    workItemName?: string;
    attachmentName?: string;
    workItemId?: number;
}

type SearchAttachmentSignature = (
    gridMetadata: GridMetaData,
    parameters: SearchParameters,
    draftId?: number,
    draft?: boolean
) => Promise<ISearchAttachmentsResult | null>;

type UseAttachmentSearchResult = [
    SearchAttachmentSignature,
    boolean
];
type UseAttachmentSearchSignature = (all?: boolean) => UseAttachmentSearchResult;

const makeAttachmentSearchUrl = (typeId: number, searchParams: URLSearchParams, draft?: boolean) => draft 
    ? `/api/v3/drafts/workitems/${typeId}/attachments/list?${searchParams}`
    : `/api/v3/attachments/${typeId}/list?${searchParams}`;

const useAttachmentSearch: UseAttachmentSearchSignature = (all) => {
    const [ loading, setLoading ] = useState<boolean>(false);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const handleSearch = async (gridMetadata: GridMetaData, parameters: SearchParameters, draftId?: number, draft?: boolean) => {
        const { workItemName, attachmentName, typeId, fileOrLink, workItemId } = parameters;
        const searchParams = createUrlSearchParams(gridMetadata);
        
        searchParams.append('all', (all ?? false).toString());
        searchParams.append('type', fileOrLink.toString());

        if (workItemName && workItemName.length > 0) {
            searchParams.append('workItemNameSearch', workItemName ?? '');
        }
        if (attachmentName && attachmentName.length > 0) {
            searchParams.append('attachmentNameSearch', attachmentName ?? '');
        }
        if (workItemId && workItemId > 0) {
            searchParams.append('wiid', workItemId.toString());
        }

        if (draftId && draftId > 0) {
            searchParams.append('draftId', draftId.toString());
        }

        setLoading(true)
        try {
            const urlRoot = makeAttachmentSearchUrl(typeId, searchParams, draft);
            const resp = await fetcher(urlRoot);

            const result = await resp.json() as ISearchAttachmentsResult;
            if (!resp.ok) {
                return null;
            }

            return result;
        } finally {
            setLoading(false);
        }
    };

    return [
        handleSearch,
        loading
    ]
};

export default useAttachmentSearch;